<template>
  <main class="single-film-prototype single-person-prototype single-film-list-prototype single-episode-list-prototype">
    <section class="hero g-0 position-relative">
      <HeroTopPanel
        :logo-src="logoPath"
        :background-src="defaultBackgroundImage"
        background-wrap-class="film-list-hero__bgWrap"
        :login-path="loginPath"
        @open-mobile-search="openMobileSearchOverlay"
      >
        <div class="row g-0 hero__grid align-items-end film-list-hero__grid">
          <div class="col hero__meta film-list-hero__meta">
            <div class="upper-container">
              <div class="user-event-container film-list-hero__meta-stack">
                <input
                  v-if="listEditMode"
                  v-model="editableListTitle"
                  class="item-title film-list-detail__title-input"
                  data-testid="episode-list-title-input"
                  type="text"
                >
                <h1 v-else class="item-title" data-testid="episode-list-title">
                  {{ episodeList.title || '' }}
                </h1>
                <p
                  v-if="episodeListLoaded && !episodeListError"
                  class="film-list-hero__meta-line"
                  data-testid="episode-list-meta"
                >
                  {{ episodeCountLabel }}
                  <span aria-hidden="true"> • </span>
                  <CuratedListLabel v-if="episodeList.is_curated" tag="span" />
                  <template v-else>
                    <span>List by </span>
                    <a data-testid="episode-list-owner-link" :href="episodeListOwnerPath">{{ episodeList.owner_name || 'Unknown' }}</a>
                  </template>
                  <span
                    aria-hidden="true"
                    class="episode-list-detail__meta-separator episode-list-detail__meta-separator--updated film-list-detail__meta-separator--updated"
                  >
                    •
                  </span>
                  <span class="episode-list-detail__meta-updated film-list-detail__meta-updated">Updated {{ updatedAtLabel }}</span>
                </p>
                <div v-if="listFavouritesEnabled" class="buttons film-list-hero__buttons">
                  <template v-if="!isAuthenticated">
                    <a class="btn single-btn person-fav-btn" data-testid="episode-list-favourite-action" :href="loginPath">
                      <div class="default-btn">
                        <i class="ion-heart"></i> Add to favourites
                      </div>
                    </a>
                  </template>
                  <template v-else>
                    <button
                      class="btn single-btn person-fav-btn film-list-hero__favourite-button"
                      data-testid="episode-list-favourite-action"
                      type="button"
                      title="List favourites are not available yet."
                    >
                      <div class="default-btn">
                        <i class="ion-heart"></i> Add to favourites
                      </div>
                    </button>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </HeroTopPanel>
    </section>

    <section class="mobile g-0 film-list-mobile-meta container">
      <div class="row g-0 item-title-mobile">
        <input
          v-if="listEditMode"
          v-model="editableListTitle"
          class="film-list-detail__title-input film-list-detail__title-input--mobile"
          data-testid="episode-list-title-input-mobile"
          type="text"
        >
        <h1 v-else data-testid="episode-list-title-mobile">{{ episodeList.title || '' }}</h1>
      </div>
      <div v-if="episodeListLoaded && !episodeListError" class="row g-0">
        <p class="film-list-hero__meta-line film-list-hero__meta-line--mobile">
          {{ episodeCountLabel }}
          <span aria-hidden="true"> • </span>
          <CuratedListLabel v-if="episodeList.is_curated" tag="span" />
          <template v-else>
            <span>List by </span>
            <a data-testid="episode-list-owner-link-mobile" :href="episodeListOwnerPath">{{ episodeList.owner_name || 'Unknown' }}</a>
          </template>
          <span
            aria-hidden="true"
            class="episode-list-detail__meta-separator episode-list-detail__meta-separator--updated film-list-detail__meta-separator--updated"
          >
            •
          </span>
          <span class="episode-list-detail__meta-updated film-list-detail__meta-updated">Updated {{ updatedAtLabel }}</span>
        </p>
      </div>
      <div class="row g-0">
        <div class="col g-0">
          <div v-if="listFavouritesEnabled" class="buttons">
            <template v-if="!isAuthenticated">
              <a class="btn single-btn" data-testid="episode-list-favourite-action-mobile" :href="loginPath">
                <div class="default-btn">
                  <i class="ion-heart"></i> Add to favourites
                </div>
              </a>
            </template>
            <template v-else>
              <button
                class="btn single-btn film-list-hero__favourite-button"
                data-testid="episode-list-favourite-action-mobile"
                type="button"
                title="List favourites are not available yet."
              >
                <div class="default-btn">
                  <i class="ion-heart"></i> Add to favourites
                </div>
              </button>
            </template>
          </div>
        </div>
      </div>
    </section>

    <div class="container g-0 mobile-nav">
      <div class="row">
        <div class="col">
          <nav class="nav">
            <ul class="film-nav">
              <li>
                <RouterLink
                  :class="{ active: activeSection === 'overview' }"
                  :to="overviewPath"
                  data-testid="episode-list-overview-tab"
                >
                  Overview
                </RouterLink>
              </li>
              <li>
                <RouterLink
                  :class="{ active: activeSection === 'comments' }"
                  :to="commentsPath"
                  data-testid="episode-list-comments-tab"
                >
                  Comments
                </RouterLink>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <div v-if="activeSection === 'overview'" class="container g-0">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-10">
          <div class="container g-0">
            <div class="row g-0">
              <div class="col g-0">
                <div class="container g-0">
                  <div class="row g-0">
                    <div
                      v-if="listEditMode || episodeList.description || episodeListLoading || episodeListError"
                      class="col g-0 bio"
                    >
                      <template v-if="episodeListLoaded && !episodeListError && listEditMode">
                        <p
                          class="film-list-detail__description-label"
                          data-testid="episode-list-description-label"
                        >
                          Overview
                        </p>
                        <textarea
                          v-model="editableListDescription"
                          class="film-list-page__description film-list-detail__description-textarea"
                          data-testid="episode-list-description-textarea"
                          rows="5"
                        />
                      </template>
                      <p
                        v-else-if="episodeListLoaded && !episodeListError && episodeList.description"
                        class="film-list-page__description"
                        data-testid="episode-list-description"
                      >
                        {{ episodeList.description }}
                      </p>
                      <p v-else-if="episodeListLoading" class="film-lists-empty" data-testid="episode-list-loading">
                        Loading list...
                      </p>
                      <p v-else-if="episodeListError" class="film-lists-empty" data-testid="episode-list-error">
                        {{ episodeListError }}
                      </p>
                    </div>
                  </div>
                </div>

                <template v-if="episodeListLoaded && !episodeListError">
                  <div
                    ref="listContentStartRef"
                    class="row g-0 sorting-buttons-row"
                    :class="{ 'sorting-buttons-row--list-editing': listEditMode }"
                  >
                    <div class="col g-0">
                      <div class="sorting-buttons">
                        <span class="sort-title">Sort:</span>
                        <div class="sorting-buttons__primary">
                          <a
                            v-for="button in primarySortButtons"
                            :key="button.key"
                            :class="[
                              button.first ? 'first-btn' : null,
                              'sorting-btn',
                              button.active ? 'active' : null,
                            ]"
                            href="#"
                            @click.prevent="setSortMode(button.key)"
                          >
                            {{ button.label }}
                          </a>
                        </div>
                        <div v-if="secondarySortButtons.length" class="sorting-buttons__secondary">
                          <a
                            v-for="button in secondarySortButtons"
                            :key="button.key"
                            :class="[
                              'sorting-btn',
                              button.active ? 'active' : null,
                            ]"
                            href="#"
                            @click.prevent="setSortMode(button.key)"
                          >
                            {{ button.label }}
                          </a>
                        </div>
                        <div class="sorting-buttons__desktop-view-row">
                          <a
                            v-for="button in viewModeButtons"
                            :key="`desktop-${button.key}`"
                            :class="[
                              button.first ? 'first-btn' : null,
                              'view-mode-btn',
                              'sorting-btn',
                              button.active ? 'active' : null,
                            ]"
                            data-testid="episode-list-desktop-view-mode-button"
                            href="#"
                            :aria-label="button.label"
                            :title="button.label"
                            @click.prevent="setViewMode(button.key)"
                          >
                            <ViewModeIcon :mode="button.key" data-testid="episode-list-view-mode-icon" />
                          </a>
                        </div>
                        <div
                          class="modal"
                          data-testid="episode-list-filter-modal"
                          :style="{ display: filtersModalOpen ? 'flex' : 'none' }"
                          @click.self="toggleFilters"
                        >
                          <button
                            class="person-compact-rating-picker__close person-filter-modal__close"
                            data-testid="episode-list-filter-modal-close"
                            type="button"
                            aria-label="Close filters"
                            @click="toggleFilters"
                          >
                            <span class="person-compact-rating-picker__close-icon ion-close" aria-hidden="true"></span>
                          </button>
                          <div class="container person-filters">
                            <div class="person-filter-modal__header">
                              <h2>Filters:</h2>
                              <button
                                class="sorting-btn person-filter-modal__apply person-filter-modal__apply-mobile"
                                data-testid="episode-list-filter-modal-apply"
                                type="button"
                                @click="toggleFilters"
                              >
                                Apply
                              </button>
                            </div>
                            <div
                              v-for="row in filterModalRows"
                              :key="row.key"
                              class="row g-0 filters-groups-row person-filter-modal__row"
                              data-testid="episode-list-filter-modal-row"
                            >
                              <div
                                v-for="group in row.groups"
                                :key="group.key"
                                :class="[
                                  'person-filter-modal__group',
                                  row.full
                                    ? 'person-filter-modal__group--full col-12'
                                    : 'person-filter-modal__group--half col-6',
                                ]"
                                data-testid="episode-list-filter-modal-group"
                              >
                                <div class="single-filter-title-row">
                                  <h3>{{ group.title }}</h3>
                                </div>
                                <div class="filter-row">
                                  <a
                                    v-for="option in group.options"
                                    :key="option.value"
                                    :class="{ active: option.active }"
                                    href="#"
                                    @click.prevent="applyFilter(group.key, option.value)"
                                  >
                                    {{ option.label }}
                                  </a>
                                  <div
                                    v-if="group.dropdownOptions?.length"
                                    class="episode-list-detail__filter-dropdown"
                                  >
                                    <select
                                      class="filmography-filter episode-list-detail__filter-select"
                                      :data-testid="group.key === 'show' ? 'episode-list-show-filter-select-modal' : null"
                                      :value="group.dropdownValue || ''"
                                      @change="applyDropdownFilter(group.key, $event.target.value)"
                                    >
                                      <option value="">All shows</option>
                                      <option
                                        v-for="option in group.dropdownOptions"
                                        :key="`modal-${group.key}-${option.value}`"
                                        :value="option.value"
                                      >
                                        {{ option.label }}
                                      </option>
                                    </select>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div class="view-mode-btns">
                          <a
                            v-for="button in viewModeButtons"
                            :key="`mobile-${button.key}`"
                            :class="[
                              button.first ? 'first-btn' : null,
                              'view-mode-btn',
                              'sorting-btn',
                              button.active ? 'active' : null,
                            ]"
                            href="#"
                            :aria-label="button.label"
                            :title="button.label"
                            @click.prevent="setViewMode(button.key)"
                          >
                            <ViewModeIcon :mode="button.key" />
                          </a>
                          <button
                            class="sorting-btn filter-btn view-mode-filter-btn"
                            data-testid="episode-list-mobile-filter-button"
                            type="button"
                            @click="toggleFilters"
                          >
                            {{ filterButtonLabel }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="!reorderMode" class="row g-0 filters-section-sm">
                    <div class="row g-0">
                      <div class="col g-0">
                        <div class="container g-0">
                          <div class="row g-0">
                            <div class="col-9 g-0 filtering-row">
                              <input
                                v-model="titleFilter"
                                class="filmography-filter"
                                data-testid="episode-list-filter-input"
                                type="search"
                                placeholder="Filter by show or episode title..."
                              >
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="episodeListCanEdit"
                    class="row g-0 list-addition-row film-list-detail__edit-row"
                  >
                    <div class="col g-0">
                      <button
                        v-if="!reorderMode"
                        class="btn-sm list-addition-btn"
                        data-testid="episode-list-edit-button"
                        type="button"
                        :disabled="resetNumbersPending"
                        @click="startListEditing"
                      >
                        {{ listEditMode ? 'Stop editing' : 'Edit list' }}
                      </button>
                      <label
                        v-if="listEditMode"
                        class="film-list-detail__public-edit"
                        data-testid="episode-list-public-edit-field"
                      >
                        <input
                          v-model="editableListPublic"
                          data-testid="episode-list-public-edit"
                          type="checkbox"
                        >
                        <span>Public</span>
                      </label>
                      <button
                        v-if="!listEditMode && !reorderMode"
                        class="btn-sm"
                        data-testid="episode-list-reorder-button"
                        type="button"
                        :disabled="resetNumbersPending"
                        @click="startReordering"
                      >
                        Reorder items
                      </button>
                      <ResetListNumbers
                        v-if="!listEditMode && !reorderMode"
                        kind="episode"
                        :pending="resetNumbersPending"
                        :notice="resetNumbersNotice"
                        @reset="resetNumbers"
                      />
                      <button
                        v-if="reorderMode"
                        class="btn-sm"
                        data-testid="episode-list-reorder-done-button"
                        type="button"
                        @click="finishReordering"
                      >
                        Done reordering
                      </button>
                      <button
                        v-if="reorderMode"
                        class="btn-sm"
                        data-testid="episode-list-reorder-save-button"
                        type="button"
                        @click="saveReordering"
                      >
                        Save new order
                      </button>
                      <button
                        v-if="reorderMode && hasReorderChanges"
                        class="btn-sm film-list-detail__discard-button"
                        data-testid="episode-list-reorder-discard-button"
                        type="button"
                        @click="discardReorderChanges"
                      >
                        Discard changes
                      </button>
                      <p
                        v-if="reorderMode"
                        class="film-list-detail__reorder-instructions"
                        data-testid="episode-list-reorder-instructions"
                      >
                        Use the arrow buttons to move items up or down.
                      </p>
                      <div v-if="listEditMode" class="film-list-detail__edit-actions">
                        <button
                          class="btn-sm"
                          data-testid="episode-list-save-button"
                          type="button"
                          @click="saveListEditing"
                        >
                          Save
                        </button>
                        <button
                          v-if="hasUnsavedChanges"
                          class="btn-sm film-list-detail__discard-button"
                          data-testid="episode-list-discard-button"
                          type="button"
                          @click="discardListEditing"
                        >
                          Discard changes
                        </button>
                      </div>
                      <p
                        v-if="showUnsavedChangesNotice"
                        class="film-list-detail__edit-warning"
                        data-testid="episode-list-unsaved-notice"
                      >
                        You have unsaved changes. Save or discard the changes.
                      </p>
                      <p
                        v-if="showSavedChangesNotice"
                        class="film-list-detail__edit-success"
                        data-testid="episode-list-saved-notice"
                        @click="dismissSavedChangesNotice"
                      >
                        Changes saved.
                      </p>
                    </div>
                  </div>

                  <div class="container g-0">
                    <div class="row g-0">
                      <div class="col g-0">
                        <div
                          v-if="reorderMode"
                          class="container g-0 person-text-only-credits-list film-list-detail__text-list film-list-detail__reorder-list"
                          data-testid="episode-list-reorder-list"
                        >
                          <p
                            v-if="reorderMoveNotice.position === 'above'"
                            class="film-list-detail__reorder-move-notice film-list-detail__reorder-move-notice--above"
                            data-testid="episode-list-reorder-notice-above"
                          >
                            {{ reorderMoveNotice.text }}
                          </p>
                          <p
                            v-if="paginatedItems.length === 0"
                            :class="{ 'person-no-results': showNoResults }"
                            data-testid="episode-list-no-items"
                          >
                            {{ noItemsMessage }}
                          </p>
                          <div
                            v-for="(item, index) in reorderPaginatedItems"
                            v-else
                            :key="item.id"
                            :class="[
                              'row',
                              'g-0',
                              'person-text-only-credit-row',
                              'film-list-detail__text-row',
                              index === reorderPaginatedItems.length - 1 ? 'person-text-only-credit-row--last' : null,
                            ]"
                            data-testid="episode-list-reorder-row"
                          >
                            <div class="col g-0">
                              <div class="person-text-only-row film-list-detail__text-row-top">
                                <span
                                  class="film-list-detail__text-number film-list-detail__reorder-number"
                                  data-testid="episode-list-reorder-number"
                                >
                                  {{ reorderDisplayNumber(index) }}
                                </span>
                                <div class="film-list-detail__reorder-position-actions">
                                  <span
                                    v-if="isFirstReorderItem(item.id)"
                                    class="film-list-detail__reorder-position-placeholder"
                                    aria-hidden="true"
                                  ></span>
                                  <button
                                    v-else
                                    class="btn-sm film-list-detail__reorder-position-button"
                                    data-testid="episode-list-reorder-move-up"
                                    type="button"
                                    title="Move item up"
                                    aria-label="Move item up"
                                    @click="moveReorderItem(item.id, -1)"
                                  >
                                    ↑
                                  </button>
                                  <button
                                    v-if="!isLastReorderItem(item.id)"
                                    class="btn-sm film-list-detail__reorder-position-button"
                                    data-testid="episode-list-reorder-move-down"
                                    type="button"
                                    title="Move item down"
                                    aria-label="Move item down"
                                    @click="moveReorderItem(item.id, 1)"
                                  >
                                    ↓
                                  </button>
                                </div>
                                <div class="film-list-detail__text-title-link episode-list-detail__text-titles">
                                  <p class="episode-list-detail__text-show-title">
                                    {{ item.episode.show_title }}
                                  </p>
                                  <div class="episode-list-detail__text-episode-line">
                                    <span
                                      v-if="item.episode.code"
                                      class="release-year person-text-only-release-year episode-list-detail__text-code"
                                    >
                                      {{ item.episode.code }}
                                    </span>
                                    <a :href="item.episode.url || '#'" class="episode-list-detail__text-episode-link">
                                      <h3 class="person-text-only-credit-film-title">
                                        {{ truncatedTitle(item.episode.title) }}
                                      </h3>
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <p
                            v-if="reorderMoveNotice.position === 'below'"
                            class="film-list-detail__reorder-move-notice film-list-detail__reorder-move-notice--below"
                            data-testid="episode-list-reorder-notice-below"
                          >
                            {{ reorderMoveNotice.text }}
                          </p>
                        </div>

                        <div
                          v-else-if="currentViewMode === '1'"
                          class="container g-0 credits-list film-list-detail__credits-list"
                          data-testid="episode-list-full-view"
                        >
                          <p
                            v-if="paginatedItems.length === 0"
                            :class="{ 'person-no-results': showNoResults }"
                            data-testid="episode-list-no-items"
                          >
                            {{ noItemsMessage }}
                          </p>
                          <template v-for="(item, index) in paginatedItems" :key="item.id">
                            <div
                              v-if="listEditMode && isListItemPendingRemoval(item.id)"
                              class="row g-0 film-list-detail__removed-placeholder-wrap"
                              data-testid="episode-list-item-removed-placeholder-wrap"
                            >
                              <div class="col g-0">
                                <div class="film-list-detail__removed-placeholder" data-testid="episode-list-item-removed-placeholder">
                                  <span class="film-list-detail__removed-placeholder-text">
                                    '{{ item.episode.title }}' is to be deleted on save.
                                  </span>
                                  <button
                                    class="btn-sm film-list-detail__discard-button film-list-detail__item-remove-button"
                                    data-testid="episode-list-item-undo-remove-button"
                                    type="button"
                                    @click="undoListItemRemoval(item.id)"
                                  >
                                    Undo
                                  </button>
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="!(listEditMode && isListItemPendingRemoval(item.id))"
                              class="row g-0 single-credit-row film-list-detail__row"
                              data-testid="episode-list-item-row"
                            >
                              <div class="col-4 g-0 film-poster-col film-list-detail__poster-col episode-list-detail__poster-col">
                                <p class="film-list-detail__number" data-testid="episode-list-item-number">
                                  {{ item.number || '–' }}
                                </p>
                                <div class="film-poster-container episode-list-detail__image-container">
                                  <a :href="item.episode.url || '#'">
                                    <img
                                      class="episode-list-detail__image"
                                      :src="item.episode.image_url || posterPlaceholderImage"
                                      :alt="`${item.episode.show_title} ${item.episode.code} ${item.episode.title} image`"
                                    >
                                  </a>
                                </div>
                              </div>
                              <div class="col g-0">
                                <div class="container g-0 single-credit-container film-list-detail__container">
                                  <div class="row g-0 credit-title-row">
                                    <div class="col g-0">
                                      <div
                                        class="episode-list-detail__title-block"
                                        data-testid="episode-list-item-title-block"
                                      >
                                        <a class="episode-list-detail__show-link" :href="item.episode.show_url || '#'">
                                          <p
                                            class="episode-list-detail__show-title"
                                            data-testid="episode-list-item-show-title"
                                          >
                                            {{ item.episode.show_title }}
                                          </p>
                                        </a>
                                        <a :href="item.episode.url || '#'">
                                          <h3
                                            class="credit-film-title episode-list-detail__episode-title"
                                            data-testid="episode-list-item-episode-title"
                                          >
                                            <span
                                              v-if="item.episode.code"
                                              class="episode-list-detail__episode-code"
                                            >
                                              {{ item.episode.code }}
                                            </span>
                                            <span class="episode-list-detail__episode-title-text">
                                              {{ truncatedTitle(item.episode.title) }}
                                            </span>
                                          </h3>
                                        </a>
                                      </div>
                                    </div>
                                  </div>
                                  <div
                                    v-if="!listEditMode"
                                    class="film-list-detail__mobile-controls"
                                    data-testid="episode-list-item-mobile-controls"
                                  >
                                    <div class="row g-0 film-list-detail__mobile-buttons-row">
                                      <div class="col g-0">
                                        <div
                                          class="person-buttons person-buttons--credit person-buttons--without-calendar"
                                          data-testid="episode-list-item-action-group-xs"
                                        >
                                          <template v-if="isAuthenticated">
                                            <button
                                              class="fs-btn left-btn"
                                              :class="{ 'btn--watched': item.episode.watched }"
                                              type="button"
                                              :disabled="episodeWatchPendingByKey[episodeInteractionKey(item)] || item.episode.watched"
                                              @click="markListItemWatched(item)"
                                            >
                                              <div class="default-btn">
                                                <i :class="listItemWatchIconClass(item)"></i>
                                              </div>
                                            </button>
                                          </template>
                                          <template v-else>
                                            <a class="fs-btn left-btn" :href="loginPath">
                                              <div class="default-btn">
                                                <i class="ion-plus"></i>
                                              </div>
                                            </a>
                                          </template>
                                          <template v-if="isAuthenticated">
                                            <button
                                              class="fs-btn right-btn"
                                              :class="{ 'btn--favourited': item.episode.favourited }"
                                              type="button"
                                              :disabled="episodeFavouritePendingByKey[episodeInteractionKey(item)]"
                                              @click="toggleListItemFavourite(item)"
                                            >
                                              <div class="default-btn">
                                                <i class="ion-heart"></i>
                                              </div>
                                            </button>
                                          </template>
                                          <template v-else>
                                            <a class="fs-btn right-btn" :href="loginPath">
                                              <div class="default-btn">
                                                <i class="ion-heart"></i>
                                              </div>
                                            </a>
                                          </template>
                                        </div>
                                      </div>
                                    </div>
                                    <div class="row g-0 film-list-detail__mobile-rating-row">
                                      <div class="col g-0">
                                        <div class="rating-stars film-list-detail__rating film-list-detail__rating--mobile-stack">
                                          <template v-if="isAuthenticated">
                                            <button
                                              class="person-credit-rating-summary"
                                              type="button"
                                              :disabled="episodeRatingPendingByKey[episodeInteractionKey(item)]"
                                              @click="openRatingPicker(item)"
                                            >
                                              <i :class="listItemRatingStarClass(item)"></i>
                                            </button>
                                          </template>
                                          <template v-else>
                                            <a class="person-credit-rating-summary" :href="loginPath">
                                              <i class="rating-star ion-ios-star-outline"></i>
                                            </a>
                                          </template>
                                          <div class="current-credits-rating">
                                            <p>{{ listItemRatingText(item) }}</p>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div v-if="!listEditMode" class="row g-0 film-list-detail__actions-row">
                                    <div class="col g-0">
                                      <div
                                        class="person-buttons person-buttons--credit person-buttons--without-calendar"
                                        data-testid="episode-list-item-action-group"
                                      >
                                        <template v-if="isAuthenticated">
                                          <button
                                            class="fs-btn left-btn"
                                            :class="{ 'btn--watched': item.episode.watched }"
                                            type="button"
                                            :disabled="episodeWatchPendingByKey[episodeInteractionKey(item)] || item.episode.watched"
                                            @click="markListItemWatched(item)"
                                          >
                                            <div class="default-btn">
                                              <i :class="listItemWatchIconClass(item)"></i>
                                            </div>
                                          </button>
                                        </template>
                                        <template v-else>
                                          <a class="fs-btn left-btn" :href="loginPath">
                                            <div class="default-btn">
                                              <i class="ion-plus"></i>
                                            </div>
                                          </a>
                                        </template>
                                        <template v-if="isAuthenticated">
                                          <button
                                            class="fs-btn right-btn"
                                            :class="{ 'btn--favourited': item.episode.favourited }"
                                            type="button"
                                            :disabled="episodeFavouritePendingByKey[episodeInteractionKey(item)]"
                                            @click="toggleListItemFavourite(item)"
                                          >
                                            <div class="default-btn">
                                              <i class="ion-heart"></i>
                                            </div>
                                          </button>
                                        </template>
                                        <template v-else>
                                          <a class="fs-btn right-btn" :href="loginPath">
                                            <div class="default-btn">
                                              <i class="ion-heart"></i>
                                            </div>
                                          </a>
                                        </template>
                                      </div>
                                    </div>
                                    <div class="col g-0">
                                      <div class="rating-stars film-list-detail__rating">
                                        <template v-if="isAuthenticated">
                                          <button
                                            class="person-credit-rating-summary"
                                            type="button"
                                            :disabled="episodeRatingPendingByKey[episodeInteractionKey(item)]"
                                            @click="openRatingPicker(item)"
                                          >
                                            <i :class="listItemRatingStarClass(item)"></i>
                                          </button>
                                        </template>
                                        <template v-else>
                                          <a class="person-credit-rating-summary" :href="loginPath">
                                            <i class="rating-star ion-ios-star-outline"></i>
                                          </a>
                                        </template>
                                        <div class="current-credits-rating">
                                          <p>{{ listItemRatingText(item) }}</p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="!listEditMode && !isListItemPendingRemoval(item.id)"
                              :class="[
                                'row',
                                'g-0',
                                'film-list-detail__actions-row-sm',
                                'person-credits-button-row-sm',
                                index === paginatedItems.length - 1 ? 'last-person-credits-button-row-sm' : null,
                              ]"
                              data-testid="episode-list-item-action-row-sm"
                            >
                              <div class="col g-0">
                                <div
                                  class="person-buttons person-buttons--credit person-buttons--without-calendar"
                                  data-testid="episode-list-item-action-group-sm"
                                >
                                  <template v-if="isAuthenticated">
                                    <button
                                      class="fs-btn left-btn"
                                      :class="{ 'btn--watched': item.episode.watched }"
                                      type="button"
                                      :disabled="episodeWatchPendingByKey[episodeInteractionKey(item)] || item.episode.watched"
                                      @click="markListItemWatched(item)"
                                    >
                                      <div class="default-btn">
                                        <i :class="listItemWatchIconClass(item)"></i>
                                      </div>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a class="fs-btn left-btn" :href="loginPath">
                                      <div class="default-btn">
                                        <i class="ion-plus"></i>
                                      </div>
                                    </a>
                                  </template>
                                  <template v-if="isAuthenticated">
                                    <button
                                      class="fs-btn right-btn"
                                      :class="{ 'btn--favourited': item.episode.favourited }"
                                      type="button"
                                      :disabled="episodeFavouritePendingByKey[episodeInteractionKey(item)]"
                                      @click="toggleListItemFavourite(item)"
                                    >
                                      <div class="default-btn">
                                        <i class="ion-heart"></i>
                                      </div>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a class="fs-btn right-btn" :href="loginPath">
                                      <div class="default-btn">
                                        <i class="ion-heart"></i>
                                      </div>
                                    </a>
                                  </template>
                                </div>
                              </div>
                              <div class="col g-0">
                                <div class="rating-stars film-list-detail__rating film-list-detail__rating--mobile">
                                  <template v-if="isAuthenticated">
                                    <button
                                      class="person-credit-rating-summary"
                                      type="button"
                                      :disabled="episodeRatingPendingByKey[episodeInteractionKey(item)]"
                                      @click="openRatingPicker(item)"
                                    >
                                      <i :class="listItemRatingStarClass(item)"></i>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a class="person-credit-rating-summary" :href="loginPath">
                                      <i class="rating-star ion-ios-star-outline"></i>
                                    </a>
                                  </template>
                                  <div class="current-credits-rating">
                                    <p>{{ listItemRatingText(item) }}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="(listEditMode || item.description) && !isListItemPendingRemoval(item.id)"
                              class="row g-0 film-list-detail__description-row-sm"
                              data-testid="episode-list-item-description-row"
                            >
                              <div class="col g-0">
                                <textarea
                                  v-if="listEditMode"
                                  v-model="editableItemDescriptions[item.id]"
                                  class="film-list-detail__description-textarea film-list-detail__item-description-textarea"
                                  data-testid="episode-list-item-description-textarea"
                                  rows="5"
                                />
                                <p v-else class="film-list-detail__description-text">
                                  <span data-testid="episode-list-item-description-body">
                                    {{ displayedListItemDescription(item) }}
                                  </span>
                                  <button
                                    v-if="hasExpandableListItemDescription(item)"
                                    class="overview-toggle"
                                    data-testid="episode-list-item-description-toggle"
                                    type="button"
                                    @click="toggleListItemDescription(item)"
                                  >
                                    <span class="overview-toggle__icon" aria-hidden="true">
                                      {{ isListItemDescriptionExpanded(item) ? '▲' : '▼' }}
                                    </span>
                                  </button>
                                </p>
                                <div
                                  v-if="listEditMode"
                                  class="film-list-detail__item-remove-row"
                                >
                                  <button
                                    class="btn-sm film-list-detail__discard-button film-list-detail__item-remove-button"
                                    data-testid="episode-list-item-remove-button"
                                    type="button"
                                    @click="removeListItemFromEditing(item.id)"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            </div>
                          </template>
                        </div>

                        <div
                          v-else-if="currentViewMode === '2'"
                          class="person-posters-grid film-list-detail__posters-grid"
                          data-testid="episode-list-posters-grid"
                        >
                          <p
                            v-if="paginatedItems.length === 0"
                            :class="{ 'person-no-results': showNoResults }"
                            data-testid="episode-list-no-items"
                          >
                            {{ noItemsMessage }}
                          </p>
                          <div
                            v-for="item in paginatedItems"
                            v-else
                            :key="item.id"
                            class="film-list-detail__poster-card episode-list-detail__poster-card"
                            data-testid="episode-list-poster-card"
                          >
                            <div class="film-list-detail__poster-media">
                              <p class="film-list-detail__poster-number">{{ item.number || '–' }}</p>
                              <a class="person-poster-link" :href="item.episode.url || '#'">
                                <img
                                  class="person-poster-grid-image episode-list-detail__grid-image"
                                  :src="item.episode.image_url || posterPlaceholderImage"
                                  :alt="`${item.episode.show_title} ${item.episode.code} ${item.episode.title} image`"
                                >
                              </a>
                            </div>
                          </div>
                        </div>

                        <div
                          v-else
                          class="container g-0 person-text-only-credits-list film-list-detail__text-list"
                          data-testid="episode-list-text-only-list"
                        >
                          <p
                            v-if="paginatedItems.length === 0"
                            :class="{ 'person-no-results': showNoResults }"
                            data-testid="episode-list-no-items"
                          >
                            {{ noItemsMessage }}
                          </p>
                          <div
                            v-for="(item, index) in paginatedItems"
                            v-else
                            :key="item.id"
                            :class="[
                              'row',
                              'g-0',
                              'person-text-only-credit-row',
                              'film-list-detail__text-row',
                              index === paginatedItems.length - 1 ? 'person-text-only-credit-row--last' : null,
                            ]"
                            data-testid="episode-list-text-row"
                          >
                            <div class="col g-0">
                              <div class="person-text-only-row film-list-detail__text-row-top">
                                <span class="film-list-detail__text-number">{{ item.number || '–' }}</span>
                                <div class="film-list-detail__text-title-link episode-list-detail__text-titles">
                                  <p
                                    class="episode-list-detail__text-show-title"
                                    data-testid="episode-list-text-show-title"
                                  >
                                    {{ item.episode.show_title }}
                                  </p>
                                  <div class="episode-list-detail__text-episode-line">
                                    <span
                                      v-if="item.episode.code"
                                      class="release-year person-text-only-release-year episode-list-detail__text-code"
                                      data-testid="episode-list-text-episode-code"
                                    >
                                      {{ item.episode.code }}
                                    </span>
                                    <a :href="item.episode.url || '#'" class="episode-list-detail__text-episode-link">
                                      <h3
                                        class="person-text-only-credit-film-title"
                                        data-testid="episode-list-text-episode-title"
                                      >
                                        {{ truncatedTitle(item.episode.title) }}
                                      </h3>
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <nav
                          v-if="effectiveNumPages > 1"
                          class="film-lists-pagination film-list-detail__pagination film-list-detail__pagination--bootstrap film-list-detail__pagination--with-mobile"
                          aria-label="Episode list pagination"
                        >
                          <ul class="pagination mb-0">
                            <li class="page-item" :class="{ disabled: currentPage <= 1 }">
                              <button class="page-link" data-testid="episode-list-prev" type="button" @click="goToPage(currentPage - 1)">Previous</button>
                            </li>
                            <li v-for="pageItem in paginationItems" :key="`episode-list-bottom-${pageItem.key}`" class="page-item" :class="{ active: pageItem.page === currentPage, disabled: pageItem.ellipsis }">
                              <span v-if="pageItem.ellipsis" class="page-link" aria-hidden="true">{{ pageItem.label }}</span>
                              <button v-else class="page-link" :data-testid="`episode-list-page-${pageItem.page}`" type="button" :aria-label="`Go to page ${pageItem.page}`" @click="goToPage(pageItem.page)">{{ pageItem.label }}</button>
                            </li>
                            <li class="page-item" :class="{ disabled: currentPage >= effectiveNumPages }">
                              <button class="page-link" data-testid="episode-list-next" type="button" @click="goToPage(currentPage + 1)">Next</button>
                            </li>
                          </ul>
                          <CompactMobilePagination
                            :current-page="currentPage"
                            :total-pages="effectiveNumPages"
                            test-id-prefix="episode-list"
                            @page-change="goToPage"
                          />
                        </nav>

                        <div
                          v-if="listEditMode && episodeListCanEdit"
                          class="film-list-detail__delete-row"
                        >
                          <button
                            class="btn-sm film-list-detail__delete-button"
                            data-testid="episode-list-delete-button"
                            type="button"
                            :disabled="listDeletePending"
                            @click="deleteEpisodeList"
                          >
                            Delete list
                          </button>
                        </div>
                      </div>

                      <div class="col-3 g-0 filters-section" data-testid="episode-list-filters-sidebar">
                        <div class="container person-filters">
                          <div class="row g-0 filters-title-row">
                            <div class="col g-0">
                              <h3>Filters:</h3>
                            </div>
                          </div>

                          <template v-for="group in filterGroups" :key="`sidebar-${group.key}`">
                            <div
                              :data-testid="(
                                group.key === 'show'
                                  ? 'episode-list-show-filter-group-sidebar'
                                  : group.key === 'season'
                                    ? 'episode-list-season-filter-group-sidebar'
                                    : null
                              )"
                            >
                              <div class="row g-0 single-filter-title-row">
                                <div class="col g-0">
                                  <h3>{{ group.title }}</h3>
                                </div>
                              </div>
                              <div class="row g-0 filter-row">
                                <div class="col g-0">
                                  <a
                                    v-for="option in group.options"
                                    :key="option.value"
                                    :class="{ active: option.active }"
                                    href="#"
                                    @click.prevent="applyFilter(group.key, option.value)"
                                  >
                                    {{ option.label }}
                                  </a>
                                  <div
                                    v-if="group.dropdownOptions?.length"
                                    class="episode-list-detail__filter-dropdown"
                                  >
                                    <select
                                      class="filmography-filter episode-list-detail__filter-select"
                                      :data-testid="group.key === 'show' ? 'episode-list-show-filter-select-sidebar' : null"
                                      :value="group.dropdownValue || ''"
                                      @change="applyDropdownFilter(group.key, $event.target.value)"
                                    >
                                      <option value="">All shows</option>
                                      <option
                                        v-for="option in group.dropdownOptions"
                                        :key="`sidebar-${group.key}-${option.value}`"
                                        :value="option.value"
                                      >
                                        {{ option.label }}
                                      </option>
                                    </select>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="d-none d-md-block col-lg-2 ad-section">
        </div>
      </div>
    </div>

    <div v-else class="container g-0" data-testid="comments-panel">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-10">
          <CommentsPanel
            :base-path="overviewPath"
            heading="Discussion"
            :is-active="activeSection === 'comments'"
            target-type="episode-list"
            :target-id="listId"
            test-id-prefix="episode-list"
          />
        </div>
        <div class="d-none d-md-block col-lg-2 ad-section">
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="selectedRatingItem"
        class="person-compact-rating-picker-overlay"
        data-testid="episode-list-rating-picker-overlay"
        @click.self="closeRatingPicker"
      >
        <div class="person-compact-rating-picker" data-testid="episode-list-rating-picker">
          <button
            class="person-compact-rating-picker__close"
            data-testid="episode-list-rating-picker-close"
            type="button"
            aria-label="Close rating picker"
            @click="closeRatingPicker"
          >
            <span
              class="person-compact-rating-picker__close-icon ion-close"
              aria-hidden="true"
            ></span>
          </button>

          <p class="person-compact-rating-picker__title">
            Rate {{ ratingPickerTitle }}
          </p>
          <div class="person-compact-rating-picker__stars">
            <button
              v-for="star in 10"
              :key="`episode-list-rating-${episodeInteractionKey(selectedRatingItem) || 'episode'}-${star}`"
              class="person-compact-rating-picker__star"
              data-testid="episode-list-rating-picker-star"
              type="button"
              :disabled="selectedRatingItem ? episodeRatingPendingByKey[episodeInteractionKey(selectedRatingItem)] : false"
              @mouseenter="previewRating(star)"
              @mouseleave="resetRatingPreview"
              @click="selectRating(star)"
            >
              <i :class="ratingPickerStarClass(star)"></i>
            </button>
          </div>
          <button
            class="person-compact-rating-picker__reset"
            data-testid="episode-list-rating-picker-reset"
            type="button"
            :disabled="!selectedRatingItem || !selectedRatingItem.episode.rating || episodeRatingPendingByKey[episodeInteractionKey(selectedRatingItem)]"
            @click="resetSelectedItemRating"
          >
            Reset rating
          </button>
        </div>
      </div>
    </Teleport>
    <MobileSearchOverlay :open="mobileSearchOpen" @close="closeMobileSearchOverlay" />
  </main>
</template>

<script setup>
import CuratedListLabel from '../components/CuratedListLabel.vue'
import ResetListNumbers from '../components/ResetListNumbers.vue'
import { useListNumberReset } from '../utils/listNumberReset'
import { usePageViewTracking } from '../analytics/pageViews'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CommentsPanel from '../components/CommentsPanel.vue'
import CompactMobilePagination from '../components/CompactMobilePagination.vue'
import HeroTopPanel from '../components/HeroTopPanel.vue'
import MobileSearchOverlay from '../components/MobileSearchOverlay.vue'
import ViewModeIcon from '../components/ViewModeIcon'
import '../assets/single-film/bootstrap-lite.css'
import '../assets/single-film/styles.css'
import '../assets/single-person/person-detail.css'
import '../assets/single-film-list/styles.css'
import '../assets/single-episode-list/styles.css'
import { useAuthStore } from '../stores/auth'
import { buildLoginPath } from '../utils/auth'
import { clipOverviewWithSuffix, hasExpandableOverview } from '../utils/overview'
import { compactPaginationItems } from '../utils/pagination'
import { buildDocumentTitle, setDocumentTitle } from '../utils/documentTitle'
import { buildMetaDescription, useMetaDescription } from '../utils/metaDescription'

const route = useRoute()
const capturePageView = usePageViewTracking()
const router = useRouter()
const auth = useAuthStore()
auth.initFromStorage()

const logoPath = '/static/spa/branding/logo.png'
const defaultBackgroundImage = '/media/films/backgrounds/default-bg.jpg'
const posterPlaceholderImage = '/static/films/img/placeholder-episode-tv.svg'
const pageSize = 50
const listFavouritesEnabled = false
const itemDescriptionDisplayLimit = 400

const mobileSearchOpen = ref(false)
const filtersModalOpen = ref(false)
const episodeListLoading = ref(false)
const episodeListLoaded = ref(false)
const episodeListError = ref('')
const episodeList = ref({
  id: null,
  title: '',
  description: '',
  owner_name: '',
  public: true,
  episode_count: 0,
  page: 1,
  page_size: pageSize,
  num_pages: 1,
  server_paginated: false,
  filter_counts: {},
  updated_at: null,
  items: [],
})
const documentTitle = computed(() => buildDocumentTitle([episodeList.value.title, 'Episode list']))
const selectedShow = ref('all')
const selectedSeason = ref('all')
const selectedActivity = ref('all')
const sortMode = ref('number-asc')
const currentViewMode = ref('1')
const titleFilter = ref('')
const currentPage = ref(1)
const listContentStartRef = ref(null)
const ignoreFilterPageReset = ref(false)
const selectedRatingItem = ref(null)
const ratingPreview = ref(0)
const listEditMode = ref(false)
const reorderMode = ref(false)
const viewModeBeforeReordering = ref('1')
const editableListTitle = ref('')
const editableListDescription = ref('')
const editableListPublic = ref(true)
const editableItemDescriptions = ref({})
const removedItemIds = ref([])
const reorderedItemIds = ref([])
const savedReorderItemIds = ref([])
const reorderItemNumbers = ref({})
const savedReorderItemNumbers = ref({})
const reorderPageLengths = ref([])
const savedReorderPageLengths = ref([])
const reorderMoveNotice = ref({ text: '', position: '' })
const showUnsavedChangesNotice = ref(false)
const showSavedChangesNotice = ref(false)
const listDeletePending = ref(false)
let savedChangesNoticeTimeoutId = null
let reorderMoveNoticeTimeoutId = null
const expandedDescriptionItemIds = ref({})
const deleteListConfirmationMessage = "Delete this list? This will permanently delete the whole list and all its list items. This deletion can't be reverted."
const listDeletedNoticeStorageKey = 'filmsomniac:listDeletedNotice'

const episodeWatchPendingByKey = ref({})
const episodeFavouritePendingByKey = ref({})
const episodeRatingPendingByKey = ref({})

function clearSavedChangesNoticeTimeout() {
  if (savedChangesNoticeTimeoutId !== null) {
    window.clearTimeout(savedChangesNoticeTimeoutId)
    savedChangesNoticeTimeoutId = null
  }
}

function dismissSavedChangesNotice() {
  clearSavedChangesNoticeTimeout()
  showSavedChangesNotice.value = false
}

function scheduleSavedChangesNoticeDismiss() {
  clearSavedChangesNoticeTimeout()
  savedChangesNoticeTimeoutId = window.setTimeout(() => {
    showSavedChangesNotice.value = false
    savedChangesNoticeTimeoutId = null
  }, 3000)
}

function clearReorderMoveNoticeTimeout() {
  if (reorderMoveNoticeTimeoutId !== null) {
    window.clearTimeout(reorderMoveNoticeTimeoutId)
    reorderMoveNoticeTimeoutId = null
  }
}

const authUsername = computed(() => auth.state.username || 'Account')
const isAuthenticated = computed(() => auth.isAuthenticated.value)
const listSlugParam = computed(() => String(route.params.listSlug || ''))
const listId = computed(() => {
  const match = listSlugParam.value.match(/^(\d+)/)
  return match ? Number.parseInt(match[1], 10) : null
})
const activeSection = computed(() => (
  route.params.section === 'comments' ? 'comments' : 'overview'
))
const loginPath = computed(() => buildLoginPath(route.fullPath || overviewPath.value))
const slugifiedTitle = computed(() => slugifyText(episodeList.value.title || '') || 'list')
const overviewPath = computed(() => (
  listId.value ? `/lists/episode/${listId.value}-${slugifiedTitle.value}` : '/lists/episode'
))
const commentsPath = computed(() => `${overviewPath.value}/comments`)
const episodeTitleDisplayLimit = 50
const hasUnsavedChanges = computed(() => (
  editableListTitle.value !== (episodeList.value.title || '')
    || editableListDescription.value !== (episodeList.value.description || '')
    || editableListPublic.value !== Boolean(episodeList.value.public)
    || episodeList.value.items.some((item) => (
      (editableItemDescriptions.value[item.id] ?? '') !== (item.description || '')
    ))
    || removedItemIds.value.length > 0
))

const hasPendingItemDeletions = computed(() => removedItemIds.value.length > 0)

const allItems = computed(() => Array.isArray(episodeList.value.items) ? episodeList.value.items : [])
const episodeListOwnerPath = computed(() => buildFilmsomniacPath(episodeList.value.owner_name))

const episodeCountLabel = computed(() => {
  const count = episodeList.value.episode_count || 0
  return `${count} ${count === 1 ? 'episode' : 'episodes'}`
})

const updatedAtLabel = computed(() => formatShortDate(episodeList.value.updated_at))

const ratingPickerTitle = computed(() => {
  const item = selectedRatingItem.value
  if (!item?.episode) {
    return 'episode'
  }
  const title = String(item.episode.title || '').trim() || 'Episode'
  const code = String(item.episode.code || '').trim()
  return code ? `${code} ${title}` : title
})

const sortButtons = computed(() => [
  { key: 'number-asc', label: 'Number 0-9', first: true, active: sortMode.value === 'number-asc' },
  { key: 'number-desc', label: 'Number 9-0', active: sortMode.value === 'number-desc' },
  { key: 'oldest', label: 'Oldest', active: sortMode.value === 'oldest' },
  { key: 'newest', label: 'Newest', active: sortMode.value === 'newest' },
])

const primarySortButtons = computed(() => sortButtons.value.slice(0, 4))

const secondarySortButtons = computed(() => [])

const viewModeButtons = computed(() => [
  {
    key: '1',
    label: 'Detailed view',
    first: true,
    active: currentViewMode.value === '1',
  },
  { key: '2', label: 'Poster grid view', active: currentViewMode.value === '2' },
  { key: '4', label: 'Text list view', active: currentViewMode.value === '4' },
])

function serverFilterCounts(groupKey) {
  if (!hasServerPagination.value) {
    return null
  }
  const counts = episodeList.value.filter_counts?.[groupKey]
  return counts && typeof counts === 'object' ? counts : null
}

function filterCount(counts, key, fallback) {
  const value = Number.parseInt(String(counts?.[key] ?? ''), 10)
  return Number.isFinite(value) ? value : fallback
}

function filterEntries(counts) {
  return Array.isArray(counts?.entries) ? counts.entries : null
}

const showCounts = computed(() => {
  const serverEntries = filterEntries(serverFilterCounts('show'))
  if (serverEntries) {
    return new Map(serverEntries.map((entry) => [
      String(entry.value || entry.label || '').trim(),
      Number.parseInt(String(entry.count || 0), 10) || 0,
    ]).filter(([key]) => key))
  }

  const counts = new Map()
  allItems.value.forEach((item) => {
    const key = String(item?.episode?.show_title || '').trim()
    if (!key) return
    counts.set(key, (counts.get(key) || 0) + 1)
  })
  return counts
})

const showFilterHasDropdown = computed(() => showCounts.value.size > 10)

const showFilterDefinitionsByCount = computed(() => (
  Array.from(showCounts.value.entries())
    .map(([value, count]) => ({
      value,
      label: value,
      count,
    }))
    .sort((left, right) => {
      if (right.count !== left.count) {
        return right.count - left.count
      }
      return compareStrings(left.label, right.label)
    })
))

const showFilterDefinitionsAlphabetical = computed(() => (
  [...showFilterDefinitionsByCount.value].sort((left, right) => compareStrings(left.label, right.label))
))

const showFilterAllCount = computed(() => (
  filterCount(serverFilterCounts('show'), 'all', allItems.value.length)
))

const showScopedItems = computed(() => {
  if (selectedShow.value === 'all') {
    return allItems.value
  }
  return allItems.value.filter((item) => String(item?.episode?.show_title || '').trim() === selectedShow.value)
})

const seasonCounts = computed(() => {
  const serverEntries = filterEntries(serverFilterCounts('season'))
  if (serverEntries) {
    return new Map(serverEntries.map((entry) => [
      Number.parseInt(String(entry.value || 0), 10) || 0,
      Number.parseInt(String(entry.count || 0), 10) || 0,
    ]).filter(([key]) => key > 0))
  }

  if (selectedShow.value === 'all') {
    return new Map()
  }

  const counts = new Map()
  showScopedItems.value.forEach((item) => {
    const seasonNumber = parsedEpisodeCode(item).season
    if (seasonNumber <= 0) {
      return
    }
    counts.set(seasonNumber, (counts.get(seasonNumber) || 0) + 1)
  })
  return counts
})

const seasonFilterDefinitions = computed(() => (
  Array.from(seasonCounts.value.entries())
    .map(([value, count]) => ({
      value: String(value),
      label: `Season ${value}`,
      count,
      seasonNumber: value,
    }))
    .sort((left, right) => left.seasonNumber - right.seasonNumber)
))

const seasonFilterOptions = computed(() => (
  buildFilterOptions(showScopedItems.value.length, selectedSeason.value, seasonFilterDefinitions.value)
))

const seasonScopedItems = computed(() => {
  if (selectedSeason.value === 'all') {
    return showScopedItems.value
  }

  return showScopedItems.value.filter((item) => (
    String(parsedEpisodeCode(item).season) === selectedSeason.value
  ))
})

const activityCounts = computed(() => ({
  all: filterCount(serverFilterCounts('activity'), 'all', seasonScopedItems.value.length),
  watched: filterCount(
    serverFilterCounts('activity'),
    'watched',
    countMatches(seasonScopedItems.value, (item) => Boolean(item?.episode?.watched)),
  ),
  unwatched: filterCount(
    serverFilterCounts('activity'),
    'unwatched',
    countMatches(seasonScopedItems.value, (item) => !item?.episode?.watched),
  ),
  rated: filterCount(
    serverFilterCounts('activity'),
    'rated',
    countMatches(seasonScopedItems.value, (item) => normalizeRatingValue(item?.episode?.rating) !== null),
  ),
  favourited: filterCount(
    serverFilterCounts('activity'),
    'favourited',
    countMatches(seasonScopedItems.value, (item) => Boolean(item?.episode?.favourited)),
  ),
}))

function buildFilterOptions(allCount, selectedValue, definitions) {
  const specificOptions = definitions
    .filter((definition) => definition.count > 0)
    .map((definition) => ({
      value: definition.value,
      label: `${definition.label} (${definition.count})`,
      active: selectedValue === definition.value,
    }))

  if (specificOptions.length === 0) {
    return [
      {
        value: 'all',
        label: `All (${allCount})`,
        active: selectedValue === 'all',
      },
    ]
  }

  return [
    {
      value: 'all',
      label: `All (${allCount})`,
      active: selectedValue === 'all',
    },
    ...specificOptions,
  ]
}

const showFilterOptions = computed(() => (
  buildFilterOptions(
    showFilterAllCount.value,
    selectedShow.value,
    showFilterHasDropdown.value
      ? showFilterDefinitionsByCount.value.slice(0, 10)
      : showFilterDefinitionsByCount.value,
  )
))

const showFilterDropdownOptions = computed(() => (
  showFilterHasDropdown.value
    ? showFilterDefinitionsAlphabetical.value.map((definition) => ({
      value: definition.value,
      label: `${definition.label} (${definition.count})`,
    }))
    : []
))

const activityFilterOptions = computed(() => {
  if (!isAuthenticated.value) {
    return []
  }

  return buildFilterOptions(activityCounts.value.all, selectedActivity.value, [
    { value: 'watched', label: 'Watched', count: activityCounts.value.watched },
    { value: 'unwatched', label: 'Unwatched', count: activityCounts.value.unwatched },
    { value: 'rated', label: 'Rated', count: activityCounts.value.rated },
    { value: 'favourited', label: 'Favourited', count: activityCounts.value.favourited },
  ])
})

const activityScopedItems = computed(() => {
  if (hasServerPagination.value) {
    return seasonScopedItems.value
  }
  if (!isAuthenticated.value || selectedActivity.value === 'all') {
    return seasonScopedItems.value
  }

  return seasonScopedItems.value.filter((item) => {
    switch (selectedActivity.value) {
      case 'watched':
        return Boolean(item?.episode?.watched)
      case 'unwatched':
        return !item?.episode?.watched
      case 'rated':
        return normalizeRatingValue(item?.episode?.rating) !== null
      case 'favourited':
        return Boolean(item?.episode?.favourited)
      default:
        return true
    }
  })
})

const normalizedTitleFilter = computed(() => titleFilter.value.trim().toLowerCase())

const filteredItems = computed(() => {
  if (normalizedTitleFilter.value.length < 2) {
    return activityScopedItems.value
  }

  return activityScopedItems.value.filter((item) => {
    const showTitle = String(item?.episode?.show_title || '').toLowerCase()
    const episodeTitle = String(item?.episode?.title || '').toLowerCase()
    const code = String(item?.episode?.code || '').toLowerCase()
    return (
      showTitle.includes(normalizedTitleFilter.value)
      || episodeTitle.includes(normalizedTitleFilter.value)
      || code.includes(normalizedTitleFilter.value)
    )
  })
})

const sortedItems = computed(() => {
  const items = [...filteredItems.value]

  switch (sortMode.value) {
    case 'number-desc':
      return items.sort((left, right) => numericCompare(right.number, left.number, right.episode.show_title, left.episode.show_title))
    case 'oldest':
      return items.sort((left, right) => numericCompare(episodeCodeValue(left), episodeCodeValue(right), left.episode.title, right.episode.title))
    case 'newest':
      return items.sort((left, right) => numericCompare(episodeCodeValue(right), episodeCodeValue(left), right.episode.title, left.episode.title))
    case 'title-asc':
      return items.sort((left, right) => compareStrings(episodeSortTitle(left), episodeSortTitle(right)))
    case 'title-desc':
      return items.sort((left, right) => compareStrings(episodeSortTitle(right), episodeSortTitle(left)))
    case 'number-asc':
    default:
      return items.sort((left, right) => numericCompare(left.number, right.number, left.episode.show_title, right.episode.show_title))
  }
})

const hasServerPagination = computed(() => (
  episodeList.value.server_paginated
    && Number.parseInt(episodeList.value.page_size, 10) === pageSize
    && Number.parseInt(episodeList.value.num_pages, 10) > 0
))

const numPages = computed(() => {
  if (hasServerPagination.value) {
    return Math.max(1, Number.parseInt(episodeList.value.num_pages, 10) || 1)
  }
  return Math.max(1, Math.ceil(sortedItems.value.length / pageSize))
})

const paginatedItems = computed(() => {
  if (hasServerPagination.value) {
    return sortedItems.value
  }
  const startIndex = (currentPage.value - 1) * pageSize
  return sortedItems.value.slice(startIndex, startIndex + pageSize)
})

const reorderOrderedItems = computed(() => {
  if (!reorderMode.value) {
    return sortedItems.value
  }

  const itemsById = new Map(sortedItems.value.map((item) => [item.id, item]))
  const orderedIds = reorderedItemIds.value.filter((id) => itemsById.has(id))
  const missingIds = sortedItems.value
    .map((item) => item.id)
    .filter((id) => !orderedIds.includes(id))

  return [...orderedIds, ...missingIds]
    .map((id) => itemsById.get(id))
    .filter(Boolean)
})

const reorderNumPages = computed(() => (
  Math.max(1, reorderPageLengths.value.length || 1)
))

const effectiveNumPages = computed(() => (
  reorderMode.value ? reorderNumPages.value : numPages.value
))
const paginationItems = computed(() => compactPaginationItems(currentPage.value, effectiveNumPages.value))

const reorderPaginatedItems = computed(() => {
  const startIndex = reorderPageStartIndex(currentPage.value)
  const currentLength = reorderPageLengths.value[currentPage.value - 1] || pageSize
  return reorderOrderedItems.value.slice(startIndex, startIndex + currentLength)
})

const hasReorderChanges = computed(() => {
  if (!reorderMode.value) {
    return false
  }

  if (reorderedItemIds.value.length !== savedReorderItemIds.value.length) {
    return true
  }

  if (reorderPageLengths.value.length !== savedReorderPageLengths.value.length) {
    return true
  }

  if (reorderPageLengths.value.some((length, index) => length !== savedReorderPageLengths.value[index])) {
    return true
  }

  if (reorderedItemIds.value.some((id, index) => id !== savedReorderItemIds.value[index])) {
    return true
  }

  return reorderedItemIds.value.some((id) => (
    reorderItemNumbers.value[id] !== savedReorderItemNumbers.value[id]
  ))
})

const noItemsMessage = computed(() => (
  activityScopedItems.value.length === 0
    ? 'No episodes on this list.'
    : 'No episodes found.'
))

const showNoResults = computed(() => (
  activityScopedItems.value.length > 0 && filteredItems.value.length === 0
))

const filterGroups = computed(() => {
  const groups = [
    {
      key: 'show',
      title: 'Show:',
      options: showFilterOptions.value,
      dropdownOptions: showFilterDropdownOptions.value,
      dropdownValue: selectedShow.value === 'all' ? '' : selectedShow.value,
    },
  ]

  if (selectedShow.value !== 'all' && seasonFilterDefinitions.value.length > 0) {
    groups.push({
      key: 'season',
      title: 'Season:',
      options: seasonFilterOptions.value,
    })
  }

  if (isAuthenticated.value) {
    groups.push({
      key: 'activity',
      title: 'Your activity',
      options: activityFilterOptions.value,
    })
  }

  return groups
})
const filterModalRows = computed(() => {
  const groups = filterGroups.value
  return groups.map((group) => ({
    key: group.key,
    groups: [group],
    full: true,
  }))
})
const filterButtonLabel = computed(() => {
  return 'Filters'
})

function pageFromRoute() {
  const rawPage = Array.isArray(route.query.page) ? route.query.page[0] : route.query.page
  const page = Number.parseInt(String(rawPage || ''), 10)
  return Number.isFinite(page) && page > 0 ? page : 1
}

function normalizedPage(pageNumber) {
  return Math.min(Math.max(pageNumber, 1), effectiveNumPages.value)
}

function queryForPage(pageNumber) {
  const query = { ...route.query }
  if (pageNumber > 1) {
    query.page = String(pageNumber)
  } else {
    delete query.page
  }
  return query
}

function syncPageRoute(pageNumber, { replace = true } = {}) {
  const location = { path: route.path, query: queryForPage(pageNumber) }
  return replace ? router.replace(location) : router.push(location)
}

function resetCurrentPage() {
  currentPage.value = 1
  void syncPageRoute(1)
}

async function scrollToListContentStart() {
  await nextTick()
  if (typeof listContentStartRef.value?.scrollIntoView === 'function') {
    listContentStartRef.value.scrollIntoView({ behavior: 'auto', block: 'start' })
  }
}

watch([selectedShow, selectedSeason, selectedActivity], () => {
  if (ignoreFilterPageReset.value) {
    return
  }
  if (!episodeListLoaded.value) {
    return
  }
  resetCurrentPage()
  if (hasServerPagination.value && !listEditMode.value && !reorderMode.value) {
    void loadEpisodeListDetail(listId.value, { page: 1, preserveFilters: true })
  }
})

watch([sortMode, titleFilter], () => {
  if (ignoreFilterPageReset.value) {
    return
  }
  if (!episodeListLoaded.value) {
    return
  }
  if (!hasServerPagination.value) {
    resetCurrentPage()
  }
})

watch(showFilterDefinitionsByCount, (definitions) => {
  if (selectedShow.value === 'all') {
    selectedSeason.value = 'all'
    return
  }
  if (!definitions.some((definition) => definition.value === selectedShow.value)) {
    selectedShow.value = 'all'
  }
}, { immediate: true })

watch(seasonFilterDefinitions, (definitions) => {
  if (selectedShow.value === 'all') {
    selectedSeason.value = 'all'
    return
  }

  if (selectedSeason.value === 'all') {
    return
  }

  if (!definitions.some((definition) => definition.value === selectedSeason.value)) {
    selectedSeason.value = 'all'
  }
}, { immediate: true })

watch(activityFilterOptions, (options) => {
  if (!isAuthenticated.value) {
    selectedActivity.value = 'all'
    return
  }
  if (!options.some((option) => option.value === selectedActivity.value)) {
    selectedActivity.value = options[0]?.value || 'all'
  }
}, { immediate: true })

watch([editableListTitle, editableListDescription, editableItemDescriptions, removedItemIds], () => {
  if (listEditMode.value && hasUnsavedChanges.value) {
    dismissSavedChangesNotice()
  }
}, { deep: true })

onBeforeUnmount(() => {
  clearSavedChangesNoticeTimeout()
  clearReorderMoveNoticeTimeout()
})

watch(numPages, (value) => {
  if (currentPage.value > value) {
    currentPage.value = value
    void syncPageRoute(value)
  }
})

watch(() => route.query.page, async () => {
  currentPage.value = normalizedPage(pageFromRoute())
  if (
    listId.value
      && episodeListLoaded.value
      && hasServerPagination.value
      && !reorderMode.value
  ) {
    await loadEpisodeListDetail(listId.value, {
      preserveFilters: true,
      preserveEditMode: listEditMode.value,
    })
  }
})

watch([listId, isAuthenticated], async ([currentListId]) => {
  if (!currentListId) {
    episodeList.value = emptyEpisodeList()
    episodeListLoaded.value = true
    episodeListError.value = 'This list could not be found.'
    return
  }
  await loadEpisodeListDetail(currentListId)
}, { immediate: true })

watch(documentTitle, setDocumentTitle, { immediate: true })

function openMobileSearchOverlay() {
  mobileSearchOpen.value = true
}

function closeMobileSearchOverlay() {
  mobileSearchOpen.value = false
}

function buildAuthHeaders() {
  const headers = {}
  if (auth.state.access) {
    headers.Authorization = `Bearer ${auth.state.access}`
  }
  return headers
}

function buildJsonAuthHeaders() {
  return {
    ...buildAuthHeaders(),
    'Content-Type': 'application/json',
  }
}

function emptyEpisodeList() {
  return {
    id: null,
    title: '',
    description: '',
    owner_name: '',
    is_owner: false,
    episode_count: 0,
    page: 1,
    page_size: pageSize,
    num_pages: 1,
    server_paginated: false,
    filter_counts: {},
    updated_at: null,
    items: [],
  }
}

function buildEditableItemDescriptions(items) {
  return Object.fromEntries(
    (Array.isArray(items) ? items : []).map((item) => [item.id, item.description || '']),
  )
}

function resetRemovedItemIds() {
  removedItemIds.value = []
}

function isListItemPendingRemoval(itemId) {
  return removedItemIds.value.includes(itemId)
}

function removeListItemFromEditing(itemId) {
  if (isListItemPendingRemoval(itemId)) {
    return
  }

  removedItemIds.value = [...removedItemIds.value, itemId]
}

function undoListItemRemoval(itemId) {
  removedItemIds.value = removedItemIds.value.filter((id) => id !== itemId)
}

function buildReorderedItemIds(items) {
  return (Array.isArray(items) ? items : []).map((item) => item.id)
}

function buildReorderItemNumbers(items) {
  return Object.fromEntries(
    (Array.isArray(items) ? items : []).map((item, index) => [
      item.id,
      Number.isFinite(Number.parseInt(item.number, 10)) ? Number.parseInt(item.number, 10) : index + 1,
    ]),
  )
}

function reorderItemNumber(item) {
  if (!item) {
    return 0
  }
  return reorderItemNumbers.value[item.id] ?? item.number ?? 0
}

function buildReorderPageLengths(itemCount) {
  const lengths = []
  let remaining = Math.max(0, itemCount)

  while (remaining > 0) {
    const nextLength = Math.min(pageSize, remaining)
    lengths.push(nextLength)
    remaining -= nextLength
  }

  return lengths.length > 0 ? lengths : [0]
}

function reorderPageStartIndex(pageNumber) {
  const boundedPageNumber = Math.max(1, pageNumber)
  return reorderPageLengths.value
    .slice(0, boundedPageNumber - 1)
    .reduce((sum, length) => sum + length, 0)
}

function reorderDisplayNumber(localIndex) {
  return reorderItemNumber(reorderPaginatedItems.value[localIndex])
}

function slugifyText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function truncatedTitle(value) {
  const normalized = String(value || '').trim()

  if (normalized.length <= episodeTitleDisplayLimit) {
    return normalized
  }

  const truncated = normalized.slice(0, episodeTitleDisplayLimit).trim()
  const lastSpaceIndex = truncated.lastIndexOf(' ')

  if (lastSpaceIndex <= 0) {
    return `${truncated}...`
  }

  return `${truncated.slice(0, lastSpaceIndex).trim()}...`
}

function listItemDescriptionKey(item) {
  return String(item?.id || '')
}

function isListItemDescriptionExpanded(item) {
  const key = listItemDescriptionKey(item)
  return Boolean(key && expandedDescriptionItemIds.value[key])
}

function hasExpandableListItemDescription(item) {
  return hasExpandableOverview(item?.description || '', itemDescriptionDisplayLimit)
}

function displayedListItemDescription(item) {
  const description = String(item?.description || '').trim()
  if (!hasExpandableListItemDescription(item) || isListItemDescriptionExpanded(item)) {
    return description
  }
  return clipOverviewWithSuffix(description, itemDescriptionDisplayLimit, '')
}

function toggleListItemDescription(item) {
  const key = listItemDescriptionKey(item)
  if (!key) {
    return
  }

  expandedDescriptionItemIds.value = {
    ...expandedDescriptionItemIds.value,
    [key]: !expandedDescriptionItemIds.value[key],
  }
}

function buildFilmsomniacPath(username) {
  const normalizedUsername = String(username || '').trim()
  return normalizedUsername ? `/filmsomniacs/${encodeURIComponent(normalizedUsername)}` : '#'
}

function formatShortDate(value) {
  if (!value) {
    return ''
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return ''
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed)
}

function normalizeRatingValue(value) {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const normalized = Number.parseInt(String(value), 10)
  if (Number.isNaN(normalized) || normalized < 1 || normalized > 10) {
    return null
  }
  return normalized
}

function countMatches(items, matcher) {
  return items.reduce((total, item) => (matcher(item) ? total + 1 : total), 0)
}

function compareStrings(left, right) {
  return String(left || '').localeCompare(String(right || ''), undefined, { sensitivity: 'base' })
}

function numericCompare(leftValue, rightValue, leftTitle, rightTitle) {
  const leftNumber = Number.parseInt(String(leftValue || 0), 10) || 0
  const rightNumber = Number.parseInt(String(rightValue || 0), 10) || 0
  if (leftNumber !== rightNumber) {
    return leftNumber - rightNumber
  }
  return compareStrings(leftTitle, rightTitle)
}

function episodeSortTitle(item) {
  return `${item?.episode?.show_title || ''} ${item?.episode?.title || ''}`.trim()
}

function parsedEpisodeCode(item) {
  const match = String(item?.episode?.code || '').match(/^(\d+)x(\d+)$/i)
  if (!match) {
    return { season: 0, episode: 0 }
  }
  return {
    season: Number.parseInt(match[1], 10) || 0,
    episode: Number.parseInt(match[2], 10) || 0,
  }
}

function episodeCodeValue(item) {
  const parsed = parsedEpisodeCode(item)
  return (parsed.season * 1000) + parsed.episode
}

function initializeFilters(items) {
  ignoreFilterPageReset.value = true
  void nextTick(() => {
    ignoreFilterPageReset.value = false
  })
  selectedShow.value = 'all'
  selectedSeason.value = 'all'
  selectedActivity.value = 'all'
  sortMode.value = 'number-asc'
  currentViewMode.value = '1'
  viewModeBeforeReordering.value = '1'
  titleFilter.value = ''
  currentPage.value = normalizedPage(pageFromRoute())
  expandedDescriptionItemIds.value = {}
  filtersModalOpen.value = false
}

function resetFiltersForNormalListMode() {
  selectedShow.value = 'all'
  selectedSeason.value = 'all'
  selectedActivity.value = 'all'
  titleFilter.value = ''
  resetCurrentPage()
  filtersModalOpen.value = false
}

function episodeListDetailQuery(pageNumber, { includeFilters = true, sort = sortMode.value } = {}) {
  const params = new URLSearchParams()
  if (pageNumber > 1) {
    params.set('page', String(pageNumber))
  }
  if (includeFilters && selectedShow.value !== 'all') {
    params.set('show', selectedShow.value)
  }
  if (includeFilters && selectedSeason.value !== 'all') {
    params.set('season', selectedSeason.value)
  }
  if (includeFilters && isAuthenticated.value && selectedActivity.value !== 'all') {
    params.set('activity', selectedActivity.value)
  }
  if (sort !== 'number-asc') {
    params.set('sort', sort)
  }
  const queryString = params.toString()
  return queryString ? `?${queryString}` : ''
}

async function loadEpisodeListDetail(currentListId, {
  page = null,
  preserveFilters = false,
  preserveEditMode = false,
  sort = sortMode.value,
} = {}) {
  const confirmPageView = capturePageView()
  const shouldPreserveEditMode = preserveEditMode && listEditMode.value
  episodeListLoading.value = true
  episodeListLoaded.value = false
  episodeListError.value = ''
  selectedRatingItem.value = null
  ratingPreview.value = 0
  if (!shouldPreserveEditMode) {
    listEditMode.value = false
  }
  reorderMode.value = false
  editableListTitle.value = ''
  editableListDescription.value = ''
  editableListPublic.value = true
  editableItemDescriptions.value = {}
  resetRemovedItemIds()
  reorderedItemIds.value = []
  savedReorderItemIds.value = []
  reorderItemNumbers.value = {}
  savedReorderItemNumbers.value = {}
  reorderPageLengths.value = []
  savedReorderPageLengths.value = []
  clearReorderMoveNotice()
  showUnsavedChangesNotice.value = false
  dismissSavedChangesNotice()

  try {
    const requestedPage = page ?? pageFromRoute()
    const detailUrl = `/api/v1/episodes/lists/${currentListId}/${episodeListDetailQuery(requestedPage, { sort })}`
    const response = await fetch(detailUrl, {
      headers: buildAuthHeaders(),
    })

    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      episodeListError.value = 'This list could not be found.'
      episodeList.value = emptyEpisodeList()
      episodeListLoaded.value = true
      return
    }

    if (response.status === 404) {
      episodeListError.value = 'This list could not be found.'
      episodeList.value = emptyEpisodeList()
      episodeListLoaded.value = true
      return
    }

    if (!response.ok) {
      episodeListError.value = 'The list could not be loaded.'
      episodeList.value = emptyEpisodeList()
      episodeListLoaded.value = true
      return
    }

    const data = await response.json()
    if (data.redirect_url) {
      window.location.assign(data.redirect_url)
      return
    }
    confirmPageView()
    episodeList.value = {
      ...emptyEpisodeList(),
      ...data,
      server_paginated: Object.prototype.hasOwnProperty.call(data, 'num_pages'),
      items: Array.isArray(data.items) ? data.items : [],
    }
    currentPage.value = normalizedPage(Number.parseInt(episodeList.value.page, 10) || requestedPage)
    if (shouldPreserveEditMode) {
      editableListTitle.value = episodeList.value.title || ''
      editableListDescription.value = episodeList.value.description || ''
      editableListPublic.value = Boolean(episodeList.value.public)
      editableItemDescriptions.value = buildEditableItemDescriptions(episodeList.value.items)
      listEditMode.value = true
    }
    if (!preserveFilters) {
      initializeFilters(episodeList.value.items)
    }
    episodeListLoaded.value = true
  } catch {
    episodeListError.value = 'The list could not be loaded.'
    episodeList.value = emptyEpisodeList()
    episodeListLoaded.value = true
  } finally {
    episodeListLoading.value = false
  }
}

async function setSortMode(mode) {
  if (hasServerPagination.value && listId.value) {
    resetCurrentPage()
    await loadEpisodeListDetail(listId.value, { page: 1, preserveFilters: true, sort: mode })
  }
  sortMode.value = mode
}

function setViewMode(mode) {
  currentViewMode.value = mode
}

function toggleFilters() {
  filtersModalOpen.value = !filtersModalOpen.value
}

function applyFilter(groupKey, value) {
  switch (groupKey) {
    case 'show':
      selectedShow.value = value
      selectedSeason.value = 'all'
      break
    case 'season':
      selectedSeason.value = value
      break
    case 'activity':
      selectedActivity.value = value
      break
    default:
      break
  }

}

function applyDropdownFilter(groupKey, value) {
  applyFilter(groupKey, value || 'all')
}

async function startListEditing() {
  if (!episodeListCanEdit.value) {
    return
  }

  if (listEditMode.value) {
    if (!hasUnsavedChanges.value) {
      editableListTitle.value = episodeList.value.title || ''
      editableListDescription.value = episodeList.value.description || ''
      editableListPublic.value = Boolean(episodeList.value.public)
      editableItemDescriptions.value = buildEditableItemDescriptions(episodeList.value.items)
      resetRemovedItemIds()
      resetFiltersForNormalListMode()
      listEditMode.value = false
      showUnsavedChangesNotice.value = false
      dismissSavedChangesNotice()
      if (hasServerPagination.value && listId.value) {
        await loadEpisodeListDetail(listId.value, { page: 1 })
      }
    } else {
      showUnsavedChangesNotice.value = true
      dismissSavedChangesNotice()
    }
    return
  }

  editableListTitle.value = episodeList.value.title || ''
  editableListDescription.value = episodeList.value.description || ''
  editableListPublic.value = Boolean(episodeList.value.public)
  editableItemDescriptions.value = buildEditableItemDescriptions(episodeList.value.items)
  resetRemovedItemIds()
  expandedDescriptionItemIds.value = {}
  currentViewMode.value = '1'
  reorderMode.value = false
  listEditMode.value = true
  showUnsavedChangesNotice.value = false
  dismissSavedChangesNotice()
}

const episodeListCanEdit = computed(() => Boolean(
  episodeList.value.can_edit ?? episodeList.value.is_owner,
))

const { resetNumbersPending, resetNumbersNotice, resetNumbers } = useListNumberReset(
  episodeList,
  buildAuthHeaders,
  () => loadEpisodeListDetail(listId.value, { page: currentPage.value, preserveFilters: true }),
)

async function startReordering() {
  if (!episodeListCanEdit.value) {
    return
  }

  titleFilter.value = ''
  await loadAllReorderItems()
  viewModeBeforeReordering.value = currentViewMode.value
  reorderedItemIds.value = buildReorderedItemIds(sortedItems.value)
  savedReorderItemIds.value = buildReorderedItemIds(sortedItems.value)
  reorderItemNumbers.value = buildReorderItemNumbers(sortedItems.value)
  savedReorderItemNumbers.value = { ...reorderItemNumbers.value }
  reorderPageLengths.value = buildReorderPageLengths(sortedItems.value.length)
  savedReorderPageLengths.value = [...reorderPageLengths.value]
  clearReorderMoveNotice()
  expandedDescriptionItemIds.value = {}
  resetCurrentPage()
  currentViewMode.value = '4'
  listEditMode.value = false
  reorderMode.value = true
  showUnsavedChangesNotice.value = false
  dismissSavedChangesNotice()
}

async function loadAllReorderItems({ includeFilters = false } = {}) {
  const totalCount = Math.max(showFilterAllCount.value, episodeList.value.episode_count || 0)
  if (!hasServerPagination.value || totalCount <= pageSize || !listId.value) {
    return
  }

  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize))
  const pageItems = new Map()

  for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
    const response = await fetch(`/api/v1/episodes/lists/${listId.value}/${episodeListDetailQuery(pageNumber, { includeFilters })}`, {
      headers: buildAuthHeaders(),
    })

    if (!response.ok) {
      return
    }

    const data = await response.json()
    pageItems.set(pageNumber, Array.isArray(data.items) ? data.items : [])
  }

  const combinedItems = []
  for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
    combinedItems.push(...(pageItems.get(pageNumber) || []))
  }

  episodeList.value = {
    ...episodeList.value,
    episode_count: combinedItems.length,
    items: combinedItems,
    page: 1,
    num_pages: Math.max(1, Math.ceil(combinedItems.length / pageSize)),
    server_paginated: false,
  }
}

function finishReordering() {
  if (!reorderMode.value) {
    return
  }

  if (!hasReorderChanges.value) {
    currentViewMode.value = viewModeBeforeReordering.value || '1'
    resetCurrentPage()
    reorderMode.value = false
    showUnsavedChangesNotice.value = false
    dismissSavedChangesNotice()
    return
  }

  showUnsavedChangesNotice.value = true

  const shouldDiscard = window.confirm('Discard all unsaved changes?')
  if (!shouldDiscard) {
    return
  }

  resetReorderChanges()
  currentViewMode.value = viewModeBeforeReordering.value || '1'
  resetCurrentPage()
  reorderMode.value = false
  showUnsavedChangesNotice.value = false
  dismissSavedChangesNotice()
}

function resetReorderChanges() {
  reorderedItemIds.value = [...savedReorderItemIds.value]
  reorderItemNumbers.value = { ...savedReorderItemNumbers.value }
  reorderPageLengths.value = [...savedReorderPageLengths.value]
  clearReorderMoveNotice()
}

function discardReorderChanges() {
  const shouldDiscard = window.confirm('Discard all unsaved changes?')
  if (!shouldDiscard) {
    return
  }

  resetReorderChanges()
}

function reorderItemIndex(itemId) {
  return reorderOrderedItems.value.findIndex((item) => item.id === itemId)
}

function isFirstReorderItem(itemId) {
  return reorderItemIndex(itemId) === 0
}

function isLastReorderItem(itemId) {
  const itemIndex = reorderItemIndex(itemId)
  return itemIndex === reorderOrderedItems.value.length - 1
}

function clearReorderMoveNotice() {
  clearReorderMoveNoticeTimeout()
  reorderMoveNotice.value = { text: '', position: '' }
}

function showReorderMoveNotice(text, position) {
  clearReorderMoveNoticeTimeout()
  reorderMoveNotice.value = { text, position }
  reorderMoveNoticeTimeoutId = window.setTimeout(() => {
    reorderMoveNotice.value = { text: '', position: '' }
    reorderMoveNoticeTimeoutId = null
  }, 5000)
}

function episodeReorderItemTitle(item) {
  return item?.episode?.title || 'List item'
}

function moveReorderItem(itemId, direction) {
  if (!reorderMode.value || !direction) {
    return
  }

  const nextIds = [...reorderedItemIds.value]
  const currentIndex = nextIds.indexOf(itemId)
  const targetIndex = currentIndex + direction

  if (currentIndex === -1 || targetIndex < 0 || targetIndex >= nextIds.length) {
    return
  }

  const currentPageStartIndex = reorderPageStartIndex(currentPage.value)
  const currentPageLength = reorderPageLengths.value[currentPage.value - 1] || pageSize
  const currentPageEndIndex = currentPageStartIndex + currentPageLength - 1
  const movedItem = reorderOrderedItems.value[currentIndex]
  const movedTitle = episodeReorderItemTitle(movedItem)

  const targetId = nextIds[targetIndex]
  const targetItem = reorderOrderedItems.value[targetIndex]
  nextIds[targetIndex] = itemId
  nextIds[currentIndex] = targetId

  reorderedItemIds.value = nextIds
  reorderItemNumbers.value = {
    ...reorderItemNumbers.value,
    [itemId]: reorderItemNumber(targetItem),
    [targetId]: reorderItemNumber(movedItem),
  }

  if (direction > 0 && currentIndex === currentPageEndIndex) {
    showReorderMoveNotice(`${movedTitle} was moved to next page to place #${targetIndex + 1}.`, 'below')
  } else if (direction < 0 && currentIndex === currentPageStartIndex) {
    showReorderMoveNotice(`${movedTitle} was moved to previous page to place #${targetIndex + 1}.`, 'above')
  } else {
    clearReorderMoveNotice()
  }
}
function discardListEditing() {
  const shouldDiscard = window.confirm('Discard all unsaved changes?')
  if (!shouldDiscard) {
    return
  }

  editableListTitle.value = episodeList.value.title || ''
  editableListDescription.value = episodeList.value.description || ''
  editableListPublic.value = Boolean(episodeList.value.public)
  editableItemDescriptions.value = buildEditableItemDescriptions(episodeList.value.items)
  resetRemovedItemIds()
  showUnsavedChangesNotice.value = false
  dismissSavedChangesNotice()
}

async function saveListEditing() {
  if (!episodeListCanEdit.value || !listId.value) {
    return
  }

  if (hasPendingItemDeletions.value) {
    const shouldDelete = window.confirm('There are items to be deleted. Save changes and delete them?')
    if (!shouldDelete) {
      return
    }
  }

  const response = await fetch(`/api/v1/episodes/lists/${listId.value}/`, {
    method: 'PATCH',
    headers: buildJsonAuthHeaders(),
    body: JSON.stringify({
      title: editableListTitle.value,
      description: editableListDescription.value,
      public: editableListPublic.value,
      removed_item_ids: removedItemIds.value,
      items: episodeList.value.items
        .filter((item) => !isListItemPendingRemoval(item.id))
        .map((item) => ({
          id: item.id,
          description: editableItemDescriptions.value[item.id] ?? '',
        })),
    }),
  })

  if (!response.ok) {
    return
  }

  const data = await response.json()
  const nextItems = Array.isArray(data.items)
    ? data.items
    : episodeList.value.items
      .filter((item) => !isListItemPendingRemoval(item.id))
      .map((item) => ({
        ...item,
        description: editableItemDescriptions.value[item.id] ?? '',
      }))
  episodeList.value = {
    ...episodeList.value,
    ...data,
    title: data.title ?? '',
    description: data.description ?? '',
    episode_count: data.episode_count ?? nextItems.length,
    items: nextItems,
  }
  editableListTitle.value = episodeList.value.title || ''
  editableListDescription.value = episodeList.value.description || ''
  editableListPublic.value = Boolean(episodeList.value.public)
  editableItemDescriptions.value = buildEditableItemDescriptions(episodeList.value.items)
  resetRemovedItemIds()
  showUnsavedChangesNotice.value = false
  showSavedChangesNotice.value = true
  scheduleSavedChangesNoticeDismiss()
}

async function deleteEpisodeList() {
  if (!episodeListCanEdit.value || !listId.value || listDeletePending.value) {
    return
  }

  const shouldDelete = window.confirm(deleteListConfirmationMessage)
  if (!shouldDelete) {
    return
  }

  listDeletePending.value = true
  try {
    const response = await fetch(`/api/v1/episodes/lists/${listId.value}/`, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
    })

    if (response.ok) {
      storeListDeletedNotice('Episode', episodeList.value.title)
      await router.push('/home/lists')
    }
  } finally {
    listDeletePending.value = false
  }
}

function storeListDeletedNotice(listMediumType, listTitle) {
  try {
    window.sessionStorage.setItem(
      listDeletedNoticeStorageKey,
      JSON.stringify({
        message: `${listMediumType} list '${String(listTitle || '').trim() || 'Untitled'}' was deleted.`,
      }),
    )
  } catch {
    // The redirect still works if browser storage is unavailable.
  }
}

async function saveReordering() {
  if (!episodeListCanEdit.value || !listId.value) {
    return
  }

  const reorderedItems = reorderOrderedItems.value
    .map((item) => ({
      id: item.id,
      description: item.description || '',
      number: reorderItemNumber(item),
    }))
    .filter((item) => item.number !== savedReorderItemNumbers.value[item.id])

  const response = await fetch(`/api/v1/episodes/lists/${listId.value}/`, {
    method: 'PATCH',
    headers: buildJsonAuthHeaders(),
    body: JSON.stringify({
      title: episodeList.value.title || '',
      description: episodeList.value.description || '',
      removed_item_ids: [],
      items: reorderedItems,
    }),
  })

  if (!response.ok) {
    return
  }

  const data = await response.json()
  const reorderedItemsById = new Map(reorderedItems.map((item) => [item.id, item]))
  const nextItems = reorderOrderedItems.value.map((item) => ({
    ...item,
    description: reorderedItemsById.get(item.id)?.description ?? item.description ?? '',
    number: reorderItemNumber(item),
  }))

  episodeList.value = {
    ...episodeList.value,
    ...data,
    title: data.title ?? '',
    description: data.description ?? '',
    items: nextItems,
  }
  reorderedItemIds.value = buildReorderedItemIds(episodeList.value.items)
  savedReorderItemIds.value = [...reorderedItemIds.value]
  reorderItemNumbers.value = buildReorderItemNumbers(episodeList.value.items)
  savedReorderItemNumbers.value = { ...reorderItemNumbers.value }
  reorderPageLengths.value = buildReorderPageLengths(episodeList.value.items.length)
  savedReorderPageLengths.value = [...reorderPageLengths.value]
  clearReorderMoveNotice()
}

function episodeInteractionKey(item) {
  if (!item?.episode) {
    return ''
  }
  const showSlug = String(item.episode.show_slug || '').trim()
  const code = String(item.episode.code || '').trim()
  if (!showSlug || !code) {
    return ''
  }
  return `${showSlug}:${code}`
}

async function goToPage(pageNumber) {
  if (pageNumber < 1 || pageNumber > effectiveNumPages.value) {
    return
  }
  if (listEditMode.value && hasUnsavedChanges.value) {
    showUnsavedChangesNotice.value = true
    dismissSavedChangesNotice()
    return
  }
  clearReorderMoveNotice()
  await syncPageRoute(pageNumber, { replace: false })
  currentPage.value = pageNumber
  await scrollToListContentStart()
}

function listItemWatchIconClass(item) {
  return item?.episode?.watched ? 'ion-checkmark' : 'ion-plus'
}

function listItemRatingStarClass(item) {
  return [
    'rating-star',
    normalizeRatingValue(item?.episode?.rating) ? 'rating-star--preview ion-ios-star' : 'ion-ios-star-outline',
  ]
}

function listItemRatingText(item) {
  const normalized = normalizeRatingValue(item?.episode?.rating)
  return normalized ? `${normalized} / 10` : '/ 10'
}

function openRatingPicker(item) {
  if (!isAuthenticated.value || !episodeInteractionKey(item)) {
    return
  }
  selectedRatingItem.value = item
  ratingPreview.value = 0
}

function closeRatingPicker() {
  selectedRatingItem.value = null
  ratingPreview.value = 0
}

function previewRating(star) {
  ratingPreview.value = star
}

function resetRatingPreview() {
  ratingPreview.value = 0
}

function ratingPickerStarClass(star) {
  const currentRating = normalizeRatingValue(selectedRatingItem.value?.episode?.rating) || 0
  const visibleRating = ratingPreview.value || currentRating
  return [
    'rating-star',
    star <= visibleRating ? 'rating-star--preview ion-ios-star' : 'ion-ios-star-outline',
  ]
}

async function markListItemWatched(item) {
  const key = episodeInteractionKey(item)
  const showSlug = item?.episode?.show_slug
  const code = item?.episode?.code
  if (!key || !showSlug || !code || !isAuthenticated.value || episodeWatchPendingByKey.value[key] || item?.episode?.watched) {
    return
  }

  episodeWatchPendingByKey.value[key] = true
  try {
    const response = await fetch(`/api/v1/episodes/${showSlug}/${code}/watch`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ source: 'L' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    const data = await response.json()
    item.episode.watched = Boolean(data.has_watched)
  } catch {
    // Leave the local watched state unchanged on request failure.
  } finally {
    episodeWatchPendingByKey.value[key] = false
  }
}

async function toggleListItemFavourite(item) {
  const key = episodeInteractionKey(item)
  const showSlug = item?.episode?.show_slug
  const code = item?.episode?.code
  if (!key || !showSlug || !code || !isAuthenticated.value || episodeFavouritePendingByKey.value[key]) {
    return
  }

  episodeFavouritePendingByKey.value[key] = true
  try {
    const response = await fetch(`/api/v1/episodes/${showSlug}/${code}/favourite`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ source: 'B' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    const data = await response.json()
    item.episode.favourited = Boolean(data.favourited)
    if (data.favourited && !item.episode.watched) {
      await markListItemWatched(item)
    }
  } catch {
    // Leave the local favourite state unchanged on request failure.
  } finally {
    episodeFavouritePendingByKey.value[key] = false
  }
}

async function selectRating(ratingValue) {
  const item = selectedRatingItem.value
  const key = episodeInteractionKey(item)
  const showSlug = item?.episode?.show_slug
  const code = item?.episode?.code
  if (!key || !showSlug || !code || !isAuthenticated.value || episodeRatingPendingByKey.value[key]) {
    return
  }

  episodeRatingPendingByKey.value[key] = true
  try {
    const response = await fetch(`/api/v1/episodes/${showSlug}/${code}/rating`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ rating: ratingValue, source: 'B' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    const data = await response.json()
    item.episode.rating = normalizeRatingValue(data.rating)
    if (item.episode.rating && !item.episode.watched) {
      await markListItemWatched(item)
    }
    closeRatingPicker()
  } catch {
    // Leave the local rating unchanged on request failure.
  } finally {
    episodeRatingPendingByKey.value[key] = false
  }
}

async function resetSelectedItemRating() {
  const item = selectedRatingItem.value
  const key = episodeInteractionKey(item)
  const showSlug = item?.episode?.show_slug
  const code = item?.episode?.code
  if (
    !key
    || !showSlug
    || !code
    || !isAuthenticated.value
    || !normalizeRatingValue(item?.episode?.rating)
    || episodeRatingPendingByKey.value[key]
  ) {
    return
  }

  episodeRatingPendingByKey.value[key] = true
  try {
    const response = await fetch(`/api/v1/episodes/${showSlug}/${code}/rating`, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    item.episode.rating = null
    closeRatingPicker()
  } catch {
    // Leave the local rating unchanged on request failure.
  } finally {
    episodeRatingPendingByKey.value[key] = false
  }
}

useMetaDescription(() => buildMetaDescription(
  'episode-list',
  episodeListLoaded.value && !episodeListError.value ? { title: episodeList.value.title } : {},
))
</script>
